import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useState,
  type ReactNode,
} from "react";

const PIN_LENGTH = 6;

export type PinViewHandle = {
  clear: () => void;
  clearAll: () => void;
};

type PinViewProps = {
  onChange: (pin: string) => void;
  /** Disable keypad while a verify request is in flight. */
  disabled?: boolean;
};

export const PinView = forwardRef<PinViewHandle, PinViewProps>(
  function PinView({ onChange, disabled = false }, ref) {
    const [pin, setPin] = useState("");

    const replace = useCallback(
      (next: string) => {
        setPin(next);
        onChange(next);
      },
      [onChange],
    );

    useImperativeHandle(ref, () => ({
      clear: () => {
        setPin("");
      },
      clearAll: () => {
        setPin("");
      },
    }));

    const append = (digit: string) => {
      if (disabled || pin.length >= PIN_LENGTH) return;
      replace(`${pin}${digit}`);
    };

    const backspace = () => {
      if (disabled || pin.length === 0) return;
      replace(pin.slice(0, -1));
    };

    return (
      <div className="flex w-full flex-col items-center">
        <div className="mb-10 flex items-center gap-3" aria-hidden="true">
          {Array.from({ length: PIN_LENGTH }, (_unused, index) => {
            const filled = index < pin.length;
            return (
              <span
                key={index}
                className={
                  filled
                    ? "h-8 w-8 rounded-full bg-primary"
                    : "h-8 w-8 rounded-full border border-light-gray-purple-2 bg-light-gray-purple-2"
                }
              />
            );
          })}
        </div>

        <div className="grid grid-cols-3 gap-x-6 gap-y-4">
          {(["1", "2", "3", "4", "5", "6", "7", "8", "9"] as const).map((digit) => (
            <KeyButton
              key={digit}
              disabled={disabled}
              onPress={() => {
                append(digit);
              }}
            >
              {digit}
            </KeyButton>
          ))}

          <span aria-hidden="true" />

          <KeyButton
            disabled={disabled}
            onPress={() => {
              append("0");
            }}
          >
            0
          </KeyButton>

          <KeyButton
            disabled={disabled}
            onPress={backspace}
            ariaLabel="ลบ"
            bordered={false}
          >
            <BackspaceIcon />
          </KeyButton>
        </div>
      </div>
    );
  },
);

function KeyButton({
  children,
  onPress,
  disabled,
  ariaLabel,
  bordered = true,
}: {
  children: ReactNode;
  onPress: () => void;
  disabled: boolean;
  ariaLabel?: string;
  bordered?: boolean;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      aria-label={ariaLabel}
      onClick={onPress}
      className={`text-24 flex h-[60px] w-[60px] items-center justify-center rounded-full font-medium text-primary disabled:opacity-40 ${
        bordered ? "border border-primary" : ""
      }`}
    >
      {children}
    </button>
  );
}

function BackspaceIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-9 w-9" aria-hidden="true">
      <path
        d="M22 3H7c-.69 0-1.23.35-1.59.88L0 12l5.41 8.11c.36.53.9.89 1.59.89h15c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-3 12.59L17.59 17 14 13.41 10.41 17 9 15.59 12.59 12 9 8.41 10.41 7 14 10.59 17.59 7 19 8.41 15.41 12 19 15.59z"
        fill="currentColor"
      />
    </svg>
  );
}
